import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  GetVehiclesListByCompanyPayload,
  GetVehiclesListByDepartmentPayload,
  ImportMassiveVehiclesResponseType,
  MultipleVehicleCreationAPI,
  UpdateVehicleAnalyticCode,
  UpdateVehicleDepartment,
  UpdateVehicleNoteTaking,
  UpdateVehicleType,
  VehicleCreationAPI,
  VehiclesAPI,
  VehiclesListType,
} from "./vehiclesAPI";
import { AxiosError } from "axios";
import { dateFormatterDayMonthYear } from "@qivia/ui";

export interface VehiclesState {
  createVehicleStatus: QueryStatus;
  createMultipleVehiclesStatus: QueryStatus;
  listAllVehiclesInDbStatus: QueryStatus;
  listAllVehiclesInDb: VehiclesListType[];
  errorMessage: string | null;
  errorMessageMassivImport: string | null;
  deactivateVehicleStatus: QueryStatus;
  vehicleUpdatedAnalyticCodeStatus: QueryStatus;
  vehicleUpdatedNoteTakingStatus: QueryStatus;
  vehicleUpdatedTypeStatus: QueryStatus;
  vehicleUpdatedDepartmentStatus: QueryStatus;
  vehiclesDateRange: [start: string, end: string] | undefined;
  // vehiclesEndDatePeriod: string | undefined;
}

const initialState: VehiclesState = {
  createVehicleStatus: "idle",
  createMultipleVehiclesStatus: "idle",
  listAllVehiclesInDbStatus: "idle",
  listAllVehiclesInDb: [],
  errorMessage: "",
  errorMessageMassivImport: "",
  deactivateVehicleStatus: "idle",
  vehicleUpdatedAnalyticCodeStatus: "idle",
  vehicleUpdatedNoteTakingStatus: "idle",
  vehicleUpdatedTypeStatus: "idle",
  vehicleUpdatedDepartmentStatus: "idle",
  vehiclesDateRange: undefined,
  // vehiclesEndDatePeriod: undefined,
};

export const vehicleAsync = createAsyncThunk(
  "vehicles/call",
  async (payload: VehicleCreationAPI, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.post<
      { errorMessage: string } | { uuid: string }
    >(`vehicle`, payload);
    if ("uuid" in response.data) {
      await thunkAPI.dispatch(
        listAllVehiclesInDbAsync({
          created: [response.data.uuid],
          companyUuid: payload.companyUuid,
        }),
      );
    }
    return response.data;
  },
);

export const updateVehicleAnalyticCodeAsync = createAsyncThunk(
  "updateVehicleAnalyticCode/call",
  async (payload: UpdateVehicleAnalyticCode, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(`/vehicle/${payload.vehicleUuid}/update_analytic_code`, {
      analyticCode: payload.analyticCode,
      companyUuid: payload.companyUuid,
    });
  },
);

export const updateVehicleNoteTakingAsync = createAsyncThunk(
  "updateVehicleNoteTaking/call",
  async (payload: UpdateVehicleNoteTaking, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(`/vehicle/${payload.vehicleUuid}/update_note_taking`, {
      noteTaking: payload.noteTaking,
      companyUuid: payload.companyUuid,
    });
  },
);

export const updateVehicleTypeAsync = createAsyncThunk(
  "updateVehicleType/call",
  async (payload: UpdateVehicleType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.put(`/vehicle/${payload.vehicleUuid}/update_vehicle_type`, {
      vehicleType: payload.vehicleType,
      companyName: payload.companyName,
    });
  },
);

export const updateVehicleDepartmentAsync = createAsyncThunk(
  "updateVehicleDepartment/call",
  async (payload: UpdateVehicleDepartment, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.post<{ departmentUuid: string | null }>(
      `/vehicle/${payload.vehicleUuid}/update_department`,
      {
        departmentName: payload.departmentName,
        companyUuid: payload.companyUuid,
      },
    );
    return response.data;
  },
);

export const importMassiveVehiclesAsync = createAsyncThunk(
  "vehicles/importMassiveVehicles",
  async (payload: MultipleVehicleCreationAPI, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    try {
      const response = await axios.post<
        { errorMessage: string } | ImportMassiveVehiclesResponseType
      >(`vehicles`, payload);
      if ("createdVehiclesUuids" in response.data) {
        await thunkAPI.dispatch(
          listAllVehiclesInDbAsync({
            created: response.data.createdVehiclesUuids,
            alreadyExisted: response.data.alreadyExistedVehiclesUuids,
            companyUuid: payload.companyUuid,
            updated: response.data.updatedVehiclesUuids,
          }),
        );
      }
      return response.data;
    } catch (e) {
      if (
        e instanceof AxiosError &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        e.response?.data?.errorMessage === "No new vehicle to create"
      ) {
        return {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          errorMessage: e.response?.data?.errorMessage as string,
        };
      } else {
        throw e;
      }
    }
  },
);

export const listAllVehiclesInDbAsync = createAsyncThunk(
  "vehiclesList/call",
  async (props: GetVehiclesListByCompanyPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });

    thunkAPI.dispatch(
      vehiclesSlice.actions.setVehiclesDateRange([
        props.from ? props.from : "",
        props.to ? props.to : new Date().toISOString(),
      ]),
    );
    const response = await axios.get<Array<VehiclesAPI>>(
      `vehicles/company/${props.companyUuid}`,
      {
        params: {
          from: props.from,
          to: props.to,
        },
      },
    );
    return response.data.map<VehiclesListType>((vehicle) => {
      const isNewVehicle = props.created?.includes(vehicle.uuid);
      const isAlreadyExisted = props.alreadyExisted?.includes(vehicle.uuid);
      const isUpdatedVehicle = props.updated?.includes(vehicle.uuid);
      return {
        ...vehicle,
        isNewVehicle,
        isAlreadyExisted,
        isUpdatedVehicle,
        additionalCostPerKm: vehicle.additionalCostPerKm
          ? vehicle.additionalCostPerKm / 100
          : null,
        model: vehicle.model ?? "",
        brand: vehicle.brand ?? "",
        brandAndModel:
          (vehicle.brand ? vehicle.brand : "") +
          (vehicle.model ? " " + vehicle.model : ""),
        lastDriver: vehicle.driverName ?? "",
        reference: vehicle.reference?.toLocaleUpperCase(),
        updatedAt: dateFormatterDayMonthYear(new Date(vehicle.updatedAt)),
      };
    });
  },
);

export const listVehiclesByDepartmentAsync = createAsyncThunk(
  "vehiclesForDepartmentUser/listVehiclesByDepartment",
  async (props: GetVehiclesListByDepartmentPayload, thunkAPI) => {
    const axios = authAxios({ departmentUuid: props.departmentUuid });

    thunkAPI.dispatch(
      vehiclesSlice.actions.setVehiclesDateRange([
        props.from ? props.from : "",
        props.to ? props.to : new Date().toISOString(),
      ]),
    );
    const response = await axios.get<Array<VehiclesAPI>>(
      `departments/vehicles/department/${props.departmentUuid}`,
      {
        params: {
          from: props.from,
          to: props.to,
        },
      },
    );

    const vehicles = response.data.map<VehiclesListType>((vehicle) => ({
      ...vehicle,
      additionalCostPerKm: vehicle.additionalCostPerKm
        ? vehicle.additionalCostPerKm / 100
        : null,
      model: vehicle.model ?? "",
      brand: vehicle.brand ?? "",
      brandAndModel:
        (vehicle.brand ? vehicle.brand : "") +
        (vehicle.model ? " " + vehicle.model : ""),
      lastDriver: vehicle.driverName ?? "",
      reference: vehicle.reference?.toLocaleUpperCase(),
    }));
    return vehicles;
  },
);

export const deactivateVehicleAsync = createAsyncThunk(
  "deactivateVehicle/call",
  async (payload: { vehicleUuid: string; companyUuid: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/vehicle/${payload.vehicleUuid}/deactivate`);
    await thunkAPI.dispatch(
      listAllVehiclesInDbAsync({ companyUuid: payload.companyUuid }),
    );
  },
);

export const vehiclesSlice = createSlice({
  name: "vehicles",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setVehiclesDateRange(
      state,
      action: PayloadAction<[start: string, end: string] | undefined>,
    ) {
      state.vehiclesDateRange = action.payload;
    },
    // setVehiclesEndDatePeriod(state, action: PayloadAction<string | undefined>) {
    //   state.vehiclesEndDatePeriod = action.payload;
    // },
    resetListAllVehiclesInDbStatus(state) {
      state.listAllVehiclesInDbStatus = "idle";
    },
    resetCreateVehicleStatus(state) {
      state.createVehicleStatus = "idle";
    },
    resetDeactivateVehicleStatus(state) {
      state.deactivateVehicleStatus = "idle";
    },
    resetVehicleUpdatedAnalyticCodeStatus(state) {
      state.vehicleUpdatedAnalyticCodeStatus = "idle";
    },
    resetVehicleUpdatedNoteTakingStatus(state) {
      state.vehicleUpdatedNoteTakingStatus = "idle";
    },
    resetVehicleUpdatedTypeStatus(state) {
      state.vehicleUpdatedTypeStatus = "idle";
    },
    resetVehicleUpdatedDepartmentStatus(state) {
      state.vehicleUpdatedDepartmentStatus = "idle";
    },
    resetCreateMultipleVehiclesStatus(state) {
      state.createMultipleVehiclesStatus = "idle";
    },
    setSelectedUuid: (state, action: PayloadAction<string | null>) => ({
      ...state,
      selectedVehicleUuid: action.payload,
    }),
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(vehicleAsync.pending, (state) => {
        state.createVehicleStatus = "processing";
      })
      .addCase(vehicleAsync.fulfilled, (state, action) => {
        state.createVehicleStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
        if ("errorMessage" in action.payload)
          state.errorMessage = action.payload.errorMessage;
        else state.errorMessage = "";
      })
      .addCase(vehicleAsync.rejected, (state) => {
        state.createVehicleStatus = "failed";
      })
      .addCase(importMassiveVehiclesAsync.pending, (state) => {
        state.createMultipleVehiclesStatus = "processing";
      })
      .addCase(importMassiveVehiclesAsync.fulfilled, (state, action) => {
        state.createMultipleVehiclesStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
        if ("errorMessage" in action.payload) {
          state.errorMessageMassivImport = action.payload.errorMessage;
        } else state.errorMessageMassivImport = "";
      })
      .addCase(importMassiveVehiclesAsync.rejected, (state) => {
        state.createMultipleVehiclesStatus = "failed";
      })
      .addCase(listAllVehiclesInDbAsync.pending, (state) => {
        state.listAllVehiclesInDbStatus = "processing";
      })
      .addCase(listAllVehiclesInDbAsync.fulfilled, (state, action) => {
        state.listAllVehiclesInDb = action.payload;
        state.listAllVehiclesInDbStatus = "success";
      })
      .addCase(listAllVehiclesInDbAsync.rejected, (state) => {
        state.listAllVehiclesInDbStatus = "failed";
      })
      .addCase(listVehiclesByDepartmentAsync.pending, (state) => {
        state.listAllVehiclesInDbStatus = "processing";
      })
      .addCase(listVehiclesByDepartmentAsync.fulfilled, (state, action) => {
        state.listAllVehiclesInDb = action.payload;
        state.listAllVehiclesInDbStatus = "success";
      })
      .addCase(listVehiclesByDepartmentAsync.rejected, (state) => {
        state.listAllVehiclesInDbStatus = "failed";
      })
      .addCase(deactivateVehicleAsync.pending, (state) => {
        state.deactivateVehicleStatus = "processing";
      })
      .addCase(deactivateVehicleAsync.fulfilled, (state) => {
        state.deactivateVehicleStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
      })
      .addCase(deactivateVehicleAsync.rejected, (state) => {
        state.deactivateVehicleStatus = "failed";
      })
      .addCase(updateVehicleAnalyticCodeAsync.pending, (state) => {
        state.vehicleUpdatedAnalyticCodeStatus = "processing";
      })
      .addCase(updateVehicleAnalyticCodeAsync.fulfilled, (state) => {
        state.vehicleUpdatedAnalyticCodeStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
      })
      .addCase(updateVehicleAnalyticCodeAsync.rejected, (state) => {
        state.vehicleUpdatedAnalyticCodeStatus = "failed";
      })
      .addCase(updateVehicleNoteTakingAsync.pending, (state) => {
        state.vehicleUpdatedNoteTakingStatus = "processing";
      })
      .addCase(updateVehicleNoteTakingAsync.fulfilled, (state) => {
        state.vehicleUpdatedNoteTakingStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
      })
      .addCase(updateVehicleNoteTakingAsync.rejected, (state) => {
        state.vehicleUpdatedNoteTakingStatus = "failed";
      })
      .addCase(updateVehicleTypeAsync.pending, (state) => {
        state.vehicleUpdatedTypeStatus = "processing";
      })
      .addCase(updateVehicleTypeAsync.fulfilled, (state) => {
        state.vehicleUpdatedTypeStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
      })
      .addCase(updateVehicleTypeAsync.rejected, (state) => {
        state.vehicleUpdatedTypeStatus = "failed";
      })
      .addCase(updateVehicleDepartmentAsync.pending, (state) => {
        state.vehicleUpdatedDepartmentStatus = "processing";
      })
      .addCase(updateVehicleDepartmentAsync.fulfilled, (state) => {
        state.vehicleUpdatedDepartmentStatus = "success";
        state.listAllVehiclesInDbStatus = "idle";
      })
      .addCase(updateVehicleDepartmentAsync.rejected, (state) => {
        state.vehicleUpdatedDepartmentStatus = "failed";
      });
  },
});

export const selectVehiclesCreatedStatus = (state: RootState) =>
  state.vehicles.createVehicleStatus;
export const selectMultipleVehiclesCreatedStatus = (state: RootState) =>
  state.vehicles.createMultipleVehiclesStatus;
export const selectListAllVehiclesInDb = (state: RootState) =>
  state.vehicles.listAllVehiclesInDb;
export const selectListAllVehiclesInDbStatus = (state: RootState) =>
  state.vehicles.listAllVehiclesInDbStatus;
export const selectErrorMessage = (state: RootState) =>
  state.vehicles.errorMessage;
export const selectErrorMessageMassivImport = (state: RootState) =>
  state.vehicles.errorMessageMassivImport;
export const selectDeactivateVehicleStatus = (state: RootState) =>
  state.vehicles.deactivateVehicleStatus;
export const selectVehicleAnalyticCodeUpdatedStatus = (state: RootState) =>
  state.vehicles.vehicleUpdatedAnalyticCodeStatus;
export const selectVehicleNoteTakingUpdatedStatus = (state: RootState) =>
  state.vehicles.vehicleUpdatedNoteTakingStatus;
export const selectVehicleTypeUpdatedStatus = (state: RootState) =>
  state.vehicles.vehicleUpdatedTypeStatus;
export const selectVehicleDepartmentUpdatedStatus = (state: RootState) =>
  state.vehicles.vehicleUpdatedDepartmentStatus;
export const selectVehiclesDateRange = (state: RootState) =>
  state.vehicles.vehiclesDateRange;
// export const selectVehiclesEndDatePeriod = (state: RootState) =>
//   state.vehicles.vehiclesEndDatePeriod;
export default vehiclesSlice.reducer;
